import { DateTime } from 'luxon';
import { FormikValues } from 'formik';

import {
  AdminAllowanceNames,
  AdminLegendVariant,
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  ContractTypes,
  expressBooking,
  IAdminBookedPharmacistDetails,
  IAdminWorkshiftDetails,
  longTermExpressBooking,
  ProfessionalReplacementTypes,
  TravelAllowanceEnums,
  WorkshiftStatus,
} from '@pharmaplan/common';

import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import { handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';
import {
  decimalConverterProxy,
  handlerForZeroes,
  parseEmptyStringProxy,
  parseFloatProxy,
} from '../../../helpers/proxyHandlers';

import useGetChangedContract from '../../LongTermContract/useGetChangedContract';
import { toJSDate } from '../../../components/Dashboard/MainCalendar/helpers';
import { rateLongTermParamsValidation } from '../../../helpers/Functions';
import { getDayMonthDateYear } from '../../../components/Reports/helpers';
import { appendTimeToDate } from '../../../helpers/workshift/functions';
import { MomentTimeFormats } from '../../../helpers/Constants';
import useExpressBookingTypes from './useExpressBookingTypes';
import { useAppDispatch } from '../../useAppDispatch';
import useSelfService from '../../useSelfService';
import strings from '../../../localization';
import { formatBookingDate, getBookingDateRange } from './bookingHelpers';

const { posted, requested, booked } = AdminLegendVariant;
const {
  emergencyFees,
  mealAllowance,
  pharmacyHourlyRate,
  accommodationAllowance,
  pharmacistHourlyRate,
} = AdminAllowanceNames;

const {
  travelAllowanceAmount,
  travelAllowanceRatePerKm,
  accommodationAllowanceType: accommodationAllowanceTypeEnum,
  travelAllowanceType,
  otherAllowance,
} = AllowancesNames;

const { meal, others, emergency } = AllowanceEnums;

interface IUseExpressBookingFormik {
  type: Exclude<AdminLegendVariant, 'total'>;
  id?: string;
}

const toDatePart = (isoDate: string) =>
  isoDate.split('T')[0];

const getRequestedWorkshiftIds = (
  type: IUseExpressBookingFormik['type'],
  allShifts: IAdminWorkshiftDetails['allShifts'],
): Array<string> => {
  if (type !== requested || !allShifts?.length) {
    return [];
  }

  return allShifts
    .filter((item) =>
      item.status === WorkshiftStatus.Requested)
    .map((item) =>
      item.workShiftId);
};

const formatDisplayDateRange = ({
  startDate,
  endDate,
}: {
  startDate?: string | null;
  endDate?: string | null;
}) => {
  if (!startDate || !endDate) {
    return '';
  }

  return `${getDayMonthDateYear(startDate)} ${strings.to} ${getDayMonthDateYear(endDate)}`;
};

// A booking's own rates take precedence over the long-term contract's rates.
const getLongTermRates = (
  bookedDetails: IAdminBookedPharmacistDetails | null,
  longTermShiftContract: IAdminWorkshiftDetails['longTermShiftContract'] | null,
) => {
  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    pharmacyWeekDayPaidHourlyRate,
    pharmacyWeekendPaidHourlyRate,
  } = longTermShiftContract ?? {};

  return {
    weekDayPaidHourlyRate: bookedDetails?.weekDayPaidHourlyRate ?? weekDayPaidHourlyRate ?? null,
    weekendPaidHourlyRate: bookedDetails?.weekendPaidHourlyRate ?? weekendPaidHourlyRate ?? null,
    pharmacyWeekDayHourlyRate: bookedDetails?.pharmacyWeekDayPaidHourlyRate ?? pharmacyWeekDayPaidHourlyRate ?? null,
    pharmacyWeekendHourlyRate: bookedDetails?.pharmacyWeekendPaidHourlyRate ?? pharmacyWeekendPaidHourlyRate ?? null,
  };
};

const useExpressBookingFormik = ({ type, id }: IUseExpressBookingFormik) => {
  const dispatch = useAppDispatch();

  const { detailsApiMap } = useExpressBookingTypes();
  const { details } = detailsApiMap[type];
  const { isSelfService } = useSelfService();
  const { getChangedItems, comparator, getEditedBookingTimes } = useGetChangedContract();

  const bookings = detailsApiMap[booked].details.bookings ?? [];
  const isEditingBooking = !!id && type === booked;
  const isPosted = type === posted;
  const longTermShiftContractId = details.longTermShiftContract?.longTermShiftContractId;
  const contractType = longTermShiftContractId ? ContractTypes.longTerm : ContractTypes.shortTerm;

  const getFormikInitialValues = () => {
    if (!id) {
      return {
        selectedDate: '',
        pharmacies: '',
        pharmacists: '',
        pharmacyId: '',
        pharmacistId: '',
        creditsLeft: 0,
        isPromoBooking: false,
        isProfessionalPromoBooking: false,
        pharmacistSelected: null,
        pharmacySelected: null,
        startTime: null,
        replacementType: ProfessionalReplacementTypes.pharmacist,
        endTime: null,
        contractType: ContractTypes.shortTerm,
        [emergencyFees]: '',
        [travelAllowanceAmount]: '',
        [travelAllowanceRatePerKm]: '',
        [otherAllowance]: null,
        [accommodationAllowance]: '',
        [mealAllowance]: '',
        [pharmacistHourlyRate]: '',
        [pharmacyHourlyRate]: '',
        [accommodationAllowanceTypeEnum]: null,
        [travelAllowanceType]: [],
        weekDayPaidHourlyRate: 0,
        weekendPaidHourlyRate: 0,
        pharmacyWeekDayPaidHourlyRate: 0,
        pharmacyWeekendPaidHourlyRate: 0,
        weekdayHours: 0,
        weekendHours: 0,
      };
    }

    const {
      pharmacyName = '',
      startDate,
      endDate,
      pharmacyId = '',
      notes,
      note,
      replacementType,
      pharmacistHourlyRate: formikPharmacistRate,
      pharmacyHourlyRate: formikPharmacyRate,
      emergencyFees: formikEmergencyFees,
      mealAllowance: formikMealAllowance,
      accommodationAllowance: formikAccommodationAllowance,
      notesForSalesOrder,
      travelAllowanceRatePerKm: formiktravelAllowanceRatePerKm,
      travelAllowanceAmount: formikTravelAllowanceAmount,
      accommodationAllowanceType,
      travelAllowanceTypes,
      otherAllowance: formikOtherAllowance,
      allShifts,
      longTermShiftContract,
    } = (details as unknown as IAdminWorkshiftDetails) ?? {};

    const bookedDetails = type === booked ? details as IAdminBookedPharmacistDetails : null;
    const { firstName, lastName } = (details as IAdminBookedPharmacistDetails) ?? {};
    const workshiftIds = getRequestedWorkshiftIds(type, allShifts);

    const {
      startDate: contractStartDate,
      endDate: contractEndDate,
      allowanceNotes,
    } = longTermShiftContract ?? {};

    const clientId = details[idStore.id as keyof typeof details];
    const name = firstName ? `${firstName} ${lastName}` : '';

    const range = isEditingBooking
      ? getBookingDateRange(bookings)
      : { startDate: contractStartDate, endDate: contractEndDate };

    const allowances = {
      [emergencyFees]: formikEmergencyFees,
      [mealAllowance]: formikMealAllowance,
      [travelAllowanceAmount]: formikTravelAllowanceAmount,
      [travelAllowanceRatePerKm]: formiktravelAllowanceRatePerKm,
      [accommodationAllowance]: formikAccommodationAllowance,
      [pharmacistHourlyRate]: formikPharmacistRate,
      [pharmacyHourlyRate]: formikPharmacyRate,
      [otherAllowance]: formikOtherAllowance,
      ...getLongTermRates(bookedDetails, longTermShiftContract),
    };

    const handledForZeros = new Proxy(allowances, handlerForZeroes);
    const handledForDecimals = new Proxy(
      handledForZeros,
      decimalConverterProxy,
    );

    return {
      isPromoBooking: bookedDetails?.isPromoBooking ?? false,
      isProfessionalPromoBooking:
        bookedDetails?.isProfessionalPromoBooking ?? false,
      allowanceNotes: bookedDetails?.allowanceNotes ?? allowanceNotes ?? '',
      notesForSalesOrder,
      displayDate: formatDisplayDateRange(range),
      selectedDate: DateTime.fromISO(startDate, { setZone: true }),
      pharmacies: '',
      pharmacists: name,
      pharmacySelected: {
        label: pharmacyName,
        id: pharmacyId,
        subtitle: '',
      },
      pharmacistSelected: isPosted
        ? null
        : {
          label: name,
          id: clientId,
          subtitle: '',
        },
      pharmacyId,
      workshiftIds,
      replacementType,
      pharmacistId: clientId,
      contractType,
      startTime: toJSDate(startDate),
      endTime: toJSDate(endDate),
      pharmacistHourlyRate,
      notes: note ?? notes ?? '',
      weekdayHours: 0,
      weekendHours: 0,
      [accommodationAllowanceTypeEnum]: accommodationAllowanceType,
      [AllowancesNames.travelAllowanceType]:
        travelAllowanceTypes?.filter?.(Boolean) ?? [],
      ...handledForDecimals,
    };
  };

  const bookLongTerm = (values: FormikValues) => {
    const { startDate, endDate } = isEditingBooking ? getBookingDateRange(bookings) : comparator();
    if (!startDate || !endDate) return;
    const { professionalCreditsLeft, hourlyRateBonus } = values ?? {};

    let weekDayPaidHourlyRate = Number(values?.weekDayPaidHourlyRate);
    let weekendPaidHourlyRate = Number(values?.weekendPaidHourlyRate);

    if (professionalCreditsLeft) {
      weekDayPaidHourlyRate += Number(hourlyRateBonus);
      weekendPaidHourlyRate += Number(hourlyRateBonus);
    }

    const rates = {
      weekDayPaidHourlyRate,
      weekendPaidHourlyRate,
      pharmacyWeekDayHourlyRate: values?.pharmacyWeekDayHourlyRate,
      pharmacyWeekendHourlyRate: values?.pharmacyWeekendHourlyRate,
    };

    const handledForEmptyString = new Proxy(rates, parseEmptyStringProxy);
    const handledForFloat = new Proxy(handledForEmptyString, parseFloatProxy);

    const isPharmacyLongParamsGreater = rateLongTermParamsValidation({
      professionalWeekday: values?.weekDayPaidHourlyRate,
      professionalWeekend: values?.weekendPaidHourlyRate,
      pharmacyWeekday: values?.pharmacyWeekDayHourlyRate,
      pharmacyWeekend: values?.pharmacyWeekendHourlyRate,
      dispatch,
      isSelfService:
        isSelfService || !!values?.creditsLeft || !!professionalCreditsLeft,
      weekdayHours: values?.weekdayHours,
      weekendHours: values?.weekendHours,
      replacementType: values?.replacementType,
    });

    if (!isPharmacyLongParamsGreater) {
      return;
    }

    const getShiftAttributes = () => {
      if (isEditingBooking) {
        return getEditedBookingTimes(bookings);
      }
      if (!id) return getChangedItems();

      const selectedShiftDates = new Set(
        details?.allShifts
          ?.filter((item) =>
            values?.workshiftIds?.includes(item.workShiftId))
          .map((shift) =>
            toDatePart(shift.startDate)),
      );

      return getChangedItems().filter((item) =>
        selectedShiftDates.has(toDatePart(item.startDate)));
    };

    dispatch(
      longTermExpressBooking({
        replacementType: values?.replacementType,
        workshiftIds: values?.workshiftIds,
        ...(isEditingBooking ? {
          bookingIds: bookings.map((booking) =>
            booking.bookingId),
        } : {}),
        shiftContractAttributes: getShiftAttributes(),
        longTermContractId: longTermShiftContractId ?? '',
        professionalId: values?.pharmacistId ?? '',
        pharmacyId: values?.pharmacyId,
        notesForSalesOrder: values.notesForSalesOrder,
        allowanceNotes: values?.allowanceNotes,
        sendEmailToProfessional: values.sendEmailToPharmacist,
        startDate: isEditingBooking ? formatBookingDate(startDate) : startDate,
        endDate: isEditingBooking ? formatBookingDate(endDate) : endDate,
        sendEmailToPharmacy: values.sendEmailToPharmacy,
        ...handledForFloat,
      }),
    );
  };

  const bookWorkshift = (values: FormikValues) => {
    const val = {
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKm],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmount],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(
        values[meal],
        values[mealAllowance],
        null,
      ),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceTypeEnum]
          === AllowanceTypesToHandle.paidValuePerNight,
        values[accommodationAllowance],
        null,
      ),
      emergencyFees: handleWorkshiftToggles(
        values[emergency],
        values[emergencyFees],
        null,
      ),
      otherAllowance: handleWorkshiftToggles(
        values[others],
        values[otherAllowance],
        null,
      ),
    };

    const handledForDecimals = new Proxy(val, parseFloatProxy);

    const startD = appendTimeToDate(
      values.selectedDate,
      values.startTime,
    ).toFormat(MomentTimeFormats.fullDate);
    const endD = appendTimeToDate(values.selectedDate, values.endTime).toFormat(
      MomentTimeFormats.fullDate,
    );

    const rest = isPosted
      ? {
        workshiftId: id,
      }
      : {
        bookingId: id,
      };

    dispatch(
      expressBooking({
        startDate: startD,
        replacementType: values?.replacementType,
        endDate: endD,
        pharmacyId: values.pharmacyId,
        [idStore.id]: values.pharmacistId,
        notes: values.notes,
        notesForSalesOrder: values.notesForSalesOrder,
        pharmacistHourlyRate: parseFloat(values.pharmacistHourlyRate ?? '0'),
        pharmacyHourlyRate: parseFloat(values.pharmacyHourlyRate ?? '0'),
        sendEmailToPharmacist: values.sendEmailToPharmacist,
        sendEmailToProfessional: values.sendEmailToPharmacist,
        sendEmailToPharmacy: values.sendEmailToPharmacy,
        travelAllowanceTypes: values[travelAllowanceType],
        accommodationAllowanceType:
          values[accommodationAllowanceTypeEnum] || null,
        ...handledForDecimals,
        ...rest,
      }),
    );
  };
  return {
    getFormikInitialValues,
    bookWorkshift,
    bookLongTerm,
  };
};

export default useExpressBookingFormik;
